import { useState } from "react";
import { motion } from "framer-motion";
import { MessageSquare, Phone, Mail, MapPin, Clock, CheckCircle2, AlertCircle, ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { CONTACT } from "../i18n/translations";
import { submitLead } from "../lib/api";
import { PageHero } from "../components/pt/PageHero";
import { useDocumentTitle } from "../lib/useDocumentTitle";

const Contact = () => {
    const { t, waUrl, lang } = useLang();
    const c = t.contact;
    const f = c.form;
    useDocumentTitle("/contacto");

    const [form, setForm] = useState({
        nombre: "",
        email: "",
        telefono: "",
        empresa: "",
        tipo: "comercio",
        mensaje: "",
    });
    const [status, setStatus] = useState(null);
    const [errorMsg, setErrorMsg] = useState("");

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        setErrorMsg("");
        try {
            await submitLead({ ...form, idioma: lang, origen: "contacto" });
            setStatus("ok");
            setForm({
                nombre: "",
                email: "",
                telefono: "",
                empresa: "",
                tipo: "comercio",
                mensaje: "",
            });
        } catch (err) {
            console.error(err);
            setStatus("err");
            setErrorMsg(err?.response?.data?.detail?.[0]?.msg || "");
        }
    };

    // Google Maps embed URL (no API key required)
    const mapsEmbed =
        "https://www.google.com/maps?q=" +
        encodeURIComponent(
            "Av. Coyoacán 1622, Col. Del Valle Sur, 03104 Benito Juárez, CDMX",
        ) +
        "&output=embed";
    const mapsLink =
        "https://www.google.com/maps/search/?api=1&query=" +
        encodeURIComponent(
            "Av. Coyoacán 1622, Col. Del Valle Sur, 03104 Benito Juárez, CDMX",
        );

    return (
        <div data-testid="contact-page">
            <PageHero
                eyebrow={c.eyebrow}
                title={c.title}
                lead={c.lead}
                testId="contact-hero"
            />

            {/* WhatsApp big banner */}
            <section className="sec tight -mt-14 relative z-10" data-testid="contact-wa-banner">
                <div className="wrap">
                    <motion.a
                        href={waUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                        className="group flex flex-col items-start justify-between gap-5 rounded-2xl bg-[#25D366] p-7 text-white shadow-[0_30px_80px_-30px_rgba(37,211,102,0.55)] transition-transform hover:scale-[1.01] md:flex-row md:items-center"
                        data-testid="contact-wa-banner-link"
                    >
                        <div className="flex items-center gap-5">
                            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-white/20">
                                <MessageSquare size={26} />
                            </div>
                            <div>
                                <div
                                    className="text-xl font-semibold"
                                    style={{ fontStretch: "118%", fontWeight: 640 }}
                                >
                                    {c.waBanner.title}
                                </div>
                                <div className="text-sm text-white/80">
                                    {c.waBanner.sub}
                                </div>
                            </div>
                        </div>
                        <span className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#128C7E] transition-colors group-hover:bg-white/95">
                            {c.waBanner.cta}
                            <ArrowUpRight size={16} />
                        </span>
                    </motion.a>
                </div>
            </section>

            {/* Contact details + Form */}
            <section className="sec" data-testid="contact-main">
                <div className="wrap grid gap-14 lg:grid-cols-[0.9fr_1.1fr]">
                    {/* Left: info */}
                    <div>
                        <h2 className="h2 mb-6" style={{ fontSize: "clamp(1.6rem, 2.6vw, 2.1rem)" }}>
                            {c.info.title}
                        </h2>

                        <ul className="space-y-4">
                            <ContactRow
                                Icon={Phone}
                                label={c.info.phoneLabel}
                                value={CONTACT.phone}
                                href={CONTACT.phoneHref}
                                testId="contact-info-phone"
                            />
                            <ContactRow
                                Icon={Mail}
                                label={c.info.emailLabel}
                                value={CONTACT.email}
                                href={CONTACT.emailHref}
                                testId="contact-info-email"
                            />
                            <ContactRow
                                Icon={MapPin}
                                label={c.info.addressLabel}
                                value={
                                    <>
                                        Av. Coyoacán 1622 P.B., Col. Del Valle Sur
                                        <br />
                                        C.P. 03104, Benito Juárez, CDMX
                                    </>
                                }
                                href={mapsLink}
                                external
                                testId="contact-info-address"
                            />
                            <ContactRow
                                Icon={Clock}
                                label={c.info.hoursLabel}
                                value={c.info.hours}
                                testId="contact-info-hours"
                            />
                        </ul>

                        {/* Map */}
                        <div className="mt-10">
                            <div className="mb-4 flex items-center justify-between gap-4">
                                <h3 className="h3">{c.map.title}</h3>
                                <a
                                    href={mapsLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-sm font-semibold text-coral-profundo hover:text-tinta"
                                >
                                    Google Maps
                                    <ArrowUpRight size={14} />
                                </a>
                            </div>
                            <div className="overflow-hidden rounded-2xl border border-linea shadow-[0_30px_80px_-40px_rgba(20,35,64,0.3)]">
                                <iframe
                                    title="Pentomino Cash — Oficinas"
                                    src={mapsEmbed}
                                    width="100%"
                                    height="320"
                                    style={{ border: 0, display: "block" }}
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    allowFullScreen
                                    data-testid="contact-map"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Right: form */}
                    <motion.form
                        onSubmit={onSubmit}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.1 }}
                        transition={{ duration: 0.6 }}
                        className="rounded-2xl border border-linea bg-white p-8 shadow-[0_30px_80px_-40px_rgba(20,35,64,0.35)]"
                        data-testid="contact-form"
                    >
                        <h2 className="h2 mb-2" style={{ fontSize: "clamp(1.5rem, 2.4vw, 1.9rem)" }}>
                            {c.form.title}
                        </h2>
                        <p className="mb-6 text-sm text-acero">{c.form.lead}</p>

                        <div className="grid gap-5 sm:grid-cols-2">
                            <Field label={f.name} required>
                                <input
                                    required
                                    type="text"
                                    value={form.nombre}
                                    onChange={update("nombre")}
                                    data-testid="form-name"
                                    className="pt-input"
                                    minLength={2}
                                />
                            </Field>
                            <Field label={f.email} required>
                                <input
                                    required
                                    type="email"
                                    value={form.email}
                                    onChange={update("email")}
                                    data-testid="form-email"
                                    className="pt-input"
                                />
                            </Field>
                            <Field label={f.phone}>
                                <input
                                    type="tel"
                                    value={form.telefono}
                                    onChange={update("telefono")}
                                    data-testid="form-phone"
                                    className="pt-input"
                                />
                            </Field>
                            <Field label={f.company}>
                                <input
                                    type="text"
                                    value={form.empresa}
                                    onChange={update("empresa")}
                                    data-testid="form-company"
                                    className="pt-input"
                                />
                            </Field>
                            <Field label={f.type} className="sm:col-span-2">
                                <select
                                    value={form.tipo}
                                    onChange={update("tipo")}
                                    data-testid="form-type"
                                    className="pt-input"
                                >
                                    {f.typeOptions.map(([v, label]) => (
                                        <option key={v} value={v}>
                                            {label}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                            <Field label={f.message} className="sm:col-span-2">
                                <textarea
                                    rows={5}
                                    value={form.mensaje}
                                    onChange={update("mensaje")}
                                    placeholder={f.messagePlaceholder}
                                    data-testid="form-message"
                                    className="pt-input"
                                />
                            </Field>
                        </div>

                        {status === "ok" && (
                            <div
                                className="mt-5 flex items-start gap-3 rounded-lg bg-[#E3F2EC] p-4 text-[#0B5A42]"
                                data-testid="form-success"
                            >
                                <CheckCircle2 size={20} className="mt-0.5 flex-none" />
                                <span>{f.ok}</span>
                            </div>
                        )}
                        {status === "err" && (
                            <div
                                className="mt-5 flex items-start gap-3 rounded-lg bg-[#FBE4E5] p-4 text-[#8E1F27]"
                                data-testid="form-error"
                            >
                                <AlertCircle size={20} className="mt-0.5 flex-none" />
                                <span>
                                    {f.err}
                                    {errorMsg && (
                                        <span className="block text-xs opacity-80">
                                            {errorMsg}
                                        </span>
                                    )}
                                </span>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={status === "sending"}
                            className="pt-btn mt-6 w-full justify-center sm:w-auto"
                            data-testid="form-submit"
                        >
                            {status === "sending" ? f.submitting : f.submit}
                        </button>
                    </motion.form>
                </div>
            </section>
        </div>
    );
};

const ContactRow = ({ Icon, label, value, href, external, testId }) => {
    const Wrapper = href ? "a" : "div";
    const wrapperProps = href
        ? {
              href,
              ...(external
                  ? { target: "_blank", rel: "noopener noreferrer" }
                  : {}),
          }
        : {};

    return (
        <li>
            <Wrapper
                {...wrapperProps}
                data-testid={testId}
                className="flex items-start gap-4 rounded-xl border border-linea bg-white p-4 transition-colors hover:border-tinta/40"
            >
                <div className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-niebla text-coral-profundo">
                    <Icon size={18} />
                </div>
                <div className="min-w-0">
                    <div className="text-xs font-semibold uppercase tracking-wider text-acero">
                        {label}
                    </div>
                    <div className="mt-0.5 text-[1rem] font-semibold text-tinta">
                        {value}
                    </div>
                </div>
            </Wrapper>
        </li>
    );
};

const Field = ({ label, children, required, className = "" }) => (
    <label className={`block ${className}`}>
        <span className="mb-1.5 block text-sm font-semibold text-tinta">
            {label}
            {required && <span className="ml-1 text-coral-profundo">*</span>}
        </span>
        {children}
    </label>
);

export default Contact;
